import { useEffect, useState } from "react";
import { ITriggeredHookResponse } from "sonobello.utilities.react";
import { Nullable } from "sonobello.utilities.react.mui";

import { CodeExchangeWithChallenge, Token } from "../../../Models/Authentication";
import { Error } from "../../../Utils/Errors";
import { useApi } from "../../../Utils/useApi";
import EnvironmentConfiguration from "../../Constants/EnvironmentConfiguration";
import { CrmOAuthConfiguration } from "../Types/OAuthConfiguration";
import { IUseGetOAuthCodeResponse, oAuthErrorMessageMap } from "./UseGetOAuthCode";

const defaultErrorMessage = "Something went wrong during the authentication process. Please contact your manager.";

class CodeExchangePayload implements CodeExchangeWithChallenge {
  readonly codeChallenge: string;
  readonly code: string;
  readonly redirectUri: string;

  constructor(codeChallenge: string, code: string, redirectUri: string) {
    this.codeChallenge = codeChallenge;
    this.code = code;
    this.redirectUri = redirectUri;
  }
}

export interface IPkceRequestPayload {
  /** The value of the code challenge to be used in exchange for an authorization token.
   * @see {@link CrmOAuthConfiguration.codeChallenge}
   */
  readonly codeChallenge: string;
}

class ConfiguredCrmOAuthConfiguration extends CrmOAuthConfiguration {
  constructor(codeChallenge: string) {
    super(
      EnvironmentConfiguration.oauth.zenoti.url,
      EnvironmentConfiguration.oauth.zenoti.clientId,
      EnvironmentConfiguration.oauth.redirectPath,
      codeChallenge
    );
  }
}

interface IUseGetGemOAuthTokenProps {
  /** The hook which obtains the oAuth code given the pkce. */
  UseGetCode: () => IUseGetOAuthCodeResponse;
}

/**
 * A hook which completes the necessary steps for an OAuth Handshake with the CRM and obtains a {@link Token} for the
 * application to use. */
const useGetGemOAuthToken = ({
  UseGetCode
}: IUseGetGemOAuthTokenProps): ITriggeredHookResponse<Token, undefined, string> => {
  const [codeExchange, setCodeExchange] = useState<Nullable<CodeExchangePayload>>(null);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    response: pkceResponse,
    error: pkceError,
    isLoading: pkceIsLoading
  } = useApi<IPkceRequestPayload>({
    url: "Legacy/V0/Token/Pkce",
    valid: submitted
  });

  const { isLoading: oAuthCodeIsLoading, errorCode, code, sendRequest } = UseGetCode();

  useEffect(() => setSubmitted(false), [submitted]);

  useEffect(() => {
    if (pkceResponse) sendRequest(new ConfiguredCrmOAuthConfiguration(pkceResponse.codeChallenge));
  }, [pkceResponse]);
  useEffect(() => {
    if (!code || !pkceResponse) return;
    setCodeExchange(
      new CodeExchangePayload(pkceResponse.codeChallenge, code, EnvironmentConfiguration.oauth.redirectPath)
    );
  }, [pkceResponse, code, pkceResponse]);

  const {
    response: codeExchangeResponse,
    error: codeExchangeError,
    isLoading: codeExchangeIsLoading
  } = useApi<Token, CodeExchangePayload>({
    method: "post",
    url: "Legacy/V0/Token/CodeExchange",
    data: codeExchange as CodeExchangeWithChallenge,
    valid: Boolean(codeExchange),
    error: Error.PostToken
  });
  useEffect(() => {
    if (pkceError || errorCode || codeExchangeError)
      setErrorMessage(
        pkceError?.message ||
          (errorCode && oAuthErrorMessageMap[errorCode]) ||
          codeExchangeError?.message ||
          defaultErrorMessage
      );
  }, [pkceError, errorCode, codeExchangeError]);

  return {
    error: errorMessage,
    isLoading: codeExchangeIsLoading || pkceIsLoading || oAuthCodeIsLoading,
    result: codeExchangeResponse || null,
    execute: () => setSubmitted(true)
  };
};

export default useGetGemOAuthToken;
